/* eslint-disable no-console */
import React, { useRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiFile, FiDownload, FiPlay, FiPause } from 'react-icons/fi';
import { Spinner } from 'react-bootstrap';
import * as Tone from 'tone';
import {
  Container,
  DocumentWrapper,
  AudioWrapper,
  ImageWrapper,
  TextWrapper,
} from './styles';
import AudioBubbleWeb from '../AudioBubble';

interface IMessageProps {
  id: string;
  key_from_server: 'income' | 'outgoing';
  message_type: 'text' | 'audio' | 'document' | 'image' | 'file';
  data: string;
  media_url: string;
  media_server_url: string;
  media_mime_type: string;
  media_size: number;
  media_server_size: number;
  latitude: number;
  longitude: number;
  created_at: Date;
}

interface IProps {
  content: IMessageProps;
}

const Message: React.FC<IProps> = ({ content }: IProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const playerRef = useRef<Tone.Player | null>(null);
  const pitchShiftRef = useRef<Tone.PitchShift | null>(null);
  const progressIntervalRef = useRef<number | null>(null);

  const loadAudio = async (url: string) => {
    try {
      setIsLoading(true);

      await initializeAudio();
      if (!playerRef.current) throw new Error('Player not initialized');

      await playerRef.current.load(url);
      setDuration(playerRef.current.buffer.duration);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  const initializeAudio = async () => {
    try {
      await Tone.start();
      if (!playerRef.current) {
        playerRef.current = new Tone.Player();
        pitchShiftRef.current = new Tone.PitchShift({
          pitch: -6,
          windowSize: 0.1,
          delayTime: 0.1,
        });

        playerRef.current.connect(pitchShiftRef.current);
        pitchShiftRef.current.toDestination();
      }
    } catch (err) {
      console.error('Error initializing audio:', err);
    }
  };

  const playAudio = async () => {
    try {
      if (!playerRef.current) {
        await loadAudio(content.media_url);
      }

      if (!playerRef.current) throw new Error('Player not initialized');

      await Tone.start();
      playerRef.current.start();
      setIsPlaying(true);

      progressIntervalRef.current = window.setInterval(() => {
        if (playerRef.current) {
          setProgress(playerRef.current.now());
        }
      }, 100);

      playerRef.current.onstop = () => {
        setIsPlaying(false);
        setProgress(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        // Reset player position
        if (playerRef.current) {
          playerRef.current.seek(0);
        }
      };
    } catch (err) {
      setIsPlaying(false);
      console.error('Error playing audio:', err);
    }
  };

  const stopAudio = () => {
    setProgress(0);
    if (playerRef.current) {
      setProgress(0);
      playerRef.current.stop();
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setIsPlaying(false);
  };

  const handlePlayPause = async () => {
    if (isPlaying) {
      stopAudio();
    } else {
      await playAudio();
    }
  };

  const handleDownloadFile = (fileUrl: string) => {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = fileUrl;
    a.setAttribute('download', fileUrl.split('/').pop() || 'file');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(a.href);
  };

  const getButtonContent = () => {
    if (isLoading)
      return (
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
        />
      );
    if (isPlaying) return <FiPause fill="#2e7d32" />;
    return <FiPlay fill="#2e7d32" />;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.stop();
        playerRef.current.dispose();
      }
      if (pitchShiftRef.current) {
        pitchShiftRef.current.dispose();
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // Reset progress when duration changes
  useEffect(() => {
    if (!isPlaying) {
      setProgress(0);
    }
  }, [duration, isPlaying]);

  return (
    <Container type={content.key_from_server} key={content.id}>
      {content.message_type === 'text' && (
        <TextWrapper>{content.data}</TextWrapper>
      )}

      {content.message_type === 'image' && content.media_server_url && (() => {
        const isHeic = content.media_mime_type === 'image/heic' || content.media_server_url.toLowerCase().endsWith('.heic');
        if (isHeic) {
          return (
            <DocumentWrapper
              onClick={() => handleDownloadFile(content.media_server_url)}
            >
              <span>
                <FiFile size={24} />
                Imagem HEIC — clique para baixar
              </span>
              <FiDownload size={24} />
            </DocumentWrapper>
          );
        }
        return (
          <ImageWrapper>
            <img src={content.media_server_url} alt="Viva Voz" />
          </ImageWrapper>
        );
      })()}

      {content.message_type === 'audio' && content.media_server_url && (
        <AudioBubbleWeb id={content.id} src={content.media_server_url} />
      )}

      {content.message_type === 'audio' && !content.media_server_url && (
        <AudioWrapper>
          <span style={{ color: '#b00' }}>Áudio não disponível</span>
        </AudioWrapper>
      )}

      {(content.message_type === 'file' ||
        content.message_type === 'document') &&
        content.media_server_url && (
          <DocumentWrapper
            onClick={() => handleDownloadFile(content.media_server_url)}
          >
            <span>
              <FiFile size={24} />
              {content.data}
            </span>
            <FiDownload size={24} />
          </DocumentWrapper>
        )}

      <span className="timestamp">
        {format(new Date(content.created_at), "dd/MM/yyyy ' às ' HH:mm")}
      </span>
    </Container>
  );
};

export default Message;
